import React, { useEffect, useMemo, useState } from "react";
import { Search, Loader2, PackageOpen, Sparkles } from "lucide-react";
import { Input } from "../components/ui/input";
import {
  fetchMyPerks,
  fetchPerkCategories,
  markPerksRead,
  studioAuth,
} from "../lib/studioApi";
import PerkCard from "../components/studio/PerkCard";
import PerkViewerModal from "../components/studio/PerkViewerModal";

const ALL_KEY = "__all__";

export default function StudioPerks() {
  const [perks, setPerks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState(ALL_KEY); // category id or ALL_KEY
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [openPerk, setOpenPerk] = useState(null);

  const me = studioAuth.getUser?.() || null;
  const isLocked =
    me?.accountSource === "subscription" && !me?.subscriptionActive;

  const load = () => {
    setLoading(true);
    Promise.all([fetchPerkCategories(), fetchMyPerks()])
      .then(([cats, list]) => {
        setCategories(cats);
        setPerks(list);
      })
      .catch(() => {
        /* toast handled by caller */
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Mark perks as read on first visit. Best-effort.
    markPerksRead().catch(() => {});
  }, []);

  const catById = useMemo(() => {
    const m = {};
    categories.forEach((c) => {
      m[c.id] = c;
    });
    return m;
  }, [categories]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return perks.filter((p) => {
      if (active !== ALL_KEY && p.categoryId !== active) return false;
      if (!q) return true;
      const cat = catById[p.categoryId];
      return (
        (p.title || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q) ||
        (cat?.name || "").toLowerCase().includes(q)
      );
    });
  }, [perks, active, query, catById]);

  // Featured perks visible regardless of filter (still respect search + cat filter).
  const featured = filtered.filter((p) => p.featured);
  const regular = filtered.filter((p) => !p.featured);

  const chips = [{ id: ALL_KEY, name: "All" }, ...categories];

  return (
    <div className="space-y-6">
      <header>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-paul-yellow mb-2">
              <Sparkles className="w-3.5 h-3.5" />
              Studio Perks
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight">
              Your members-only library.
            </h1>
            <p className="text-white/55 text-sm md:text-base mt-1.5 max-w-xl">
              Sample packs, lessons, behind-the-scenes content and downloadable resources — curated for you.
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="perks-search-input"
            placeholder="Search perks…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 h-11 bg-[#10162a] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
          />
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mt-4" data-testid="perks-chips">
          {chips.map((c) => {
            const isActive = active === c.id;
            return (
              <button
                key={c.id}
                type="button"
                data-testid={`perks-chip-${c.id === ALL_KEY ? "all" : c.slug || c.id}`}
                onClick={() => setActive(c.id)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors border ${
                  isActive
                    ? "bg-paul-yellow text-navy border-paul-yellow"
                    : "bg-transparent text-white/70 border-white/15 hover:text-white hover:border-white/30"
                }`}
                style={isActive ? { color: "#0b1535" } : undefined}
              >
                {c.name}
              </button>
            );
          })}
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-white/50">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <>
          {isLocked && (
            <div
              data-testid="perks-locked-banner"
              className="rounded-2xl border border-amber-500/40 bg-amber-500/10 px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
            >
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-bold text-amber-100">Your subscription has expired</div>
                  <div className="text-xs text-amber-200/70 mt-0.5">
                    Reactivate to unlock Watch and Download on every perk again.
                  </div>
                </div>
              </div>
              <a
                href="/studio/profile"
                className="inline-flex items-center justify-center bg-paul-yellow text-navy font-bold text-sm px-4 py-2 rounded-lg hover:brightness-105 whitespace-nowrap"
                style={{ color: "#0b1535" }}
              >
                Reactivate to access
              </a>
            </div>
          )}
          {filtered.length === 0 ? (
        <div
          data-testid="perks-empty"
          className="bg-[#0f1322] border border-white/5 rounded-2xl p-12 text-center"
        >
          <PackageOpen className="w-10 h-10 mx-auto mb-3 text-white/20" strokeWidth={1.5} />
          <h2 className="text-lg font-bold text-white mb-1">
            {query ? "No perks match your search." : "Nothing here yet."}
          </h2>
          <p className="text-white/50 text-sm">
            {query
              ? "Try a different keyword or clear the filters."
              : "Paul is curating sample packs, lessons and resources. Check back soon."}
          </p>
        </div>
      ) : (
        <>
          {featured.length > 0 && (
            <section data-testid="perks-featured-grid" className="space-y-3">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-white/60">
                <Sparkles className="w-3.5 h-3.5 text-paul-yellow" />
                Featured
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {featured.map((p) => (
                  <PerkCard
                    key={p.id}
                    perk={p}
                    category={catById[p.categoryId]}
                    onOpen={setOpenPerk}
                    locked={isLocked}
                  />
                ))}
              </div>
            </section>
          )}

          {regular.length > 0 && (
            <section data-testid="perks-grid" className="space-y-3">
              {featured.length > 0 && (
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-white/60">
                  All perks
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {regular.map((p) => (
                  <PerkCard
                    key={p.id}
                    perk={p}
                    category={catById[p.categoryId]}
                    onOpen={setOpenPerk}
                    locked={isLocked}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
        </>
      )}

      <PerkViewerModal
        perk={openPerk}
        category={openPerk ? catById[openPerk.categoryId] : null}
        open={!!openPerk}
        onOpenChange={(o) => !o && setOpenPerk(null)}
      />
    </div>
  );
}
